import { useState } from "react";
import { z } from "zod";
import Navbar from "src/components/Navbar";
import Footer from "src/components/Footer";
import { Card, CardContent } from "src/components/ui/Card";
import { Button } from "src/components/ui/Button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "src/components/ui/Tabs";
import { Search, Sparkles, CheckCircle } from "lucide-react";
import useScrollToTop from "src/hooks/useScrollToTop";
import { emailSchema, phoneNumberSchema, nameSchema } from "src/services/errorHandler";
import StudentParentForm from "src/components/forms/StudentParentForm";

// Demo booking form schema
const demoBookingSchema = z.object({
  parentName: nameSchema,
  studentName: nameSchema,
  parentPhone: phoneNumberSchema,
  email: emailSchema,
  grade: z.string().min(1, { message: "Please select a grade" }),
  primaryGoals: z.array(z.string()).min(1, { message: "Please select at least one goal" }),
});

const TutorSearchRedesigned = () => {
  const [activeTab, setActiveTab] = useState("match");
  const [formData, setFormData] = useState({
    parentName: "",
    studentName: "",
    parentPhone: "",
    email: "",
    grade: "",
    primaryGoals: [],
    services: []
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  useScrollToTop();

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Real-time validation - clear errors when valid, show errors when invalid
    try {
      if (field === 'parentName' || field === 'studentName') {
        nameSchema.parse(value);
        // Clear error if validation passes
        setFormErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[field];
          return newErrors;
        });
      } else if (field === 'parentPhone') {
        phoneNumberSchema.parse(value);
        setFormErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[field];
          return newErrors;
        });
      } else if (field === 'email') {
        emailSchema.parse(value);
        setFormErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[field];
          return newErrors;
        });
      } else if (field === 'grade') {
        if (value.trim() !== '') {
          setFormErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors[field];
            return newErrors;
          });
        }
      }
    } catch (error) {
      if (error instanceof z.ZodError && value.trim() !== '') {
        // Only show error if field is not empty
        const errorMessage = error.errors[0]?.message;
        if (errorMessage) {
          setFormErrors(prev => ({
            ...prev,
            [field]: errorMessage
          }));
        }
      } else if (value.trim() === '') {
        // Clear error if field is empty (don't show validation errors for empty fields)
        setFormErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[field];
          return newErrors;
        });
      }
    }
  };

  const handleCheckboxChange = (field: string, value: string, checked: boolean) => {
    const updatedArray = checked 
      ? [...(formData[field] as string[]), value]
      : (formData[field] as string[]).filter(item => item !== value);
    
    setFormData(prev => ({
      ...prev,
      [field]: updatedArray
    }));

    // Real-time validation for primaryGoals
    if (field === 'primaryGoals') {
      if (updatedArray.length > 0) {
        // Clear error if at least one goal is selected
        setFormErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[field];
          return newErrors;
        });
      } else {
        // Show error if no goals are selected
        setFormErrors(prev => ({
          ...prev,
          [field]: "Please select at least one goal"
        }));
      }
    }
  };

  const handleBookDemo = () => {
    try {
      // Clear previous errors
      setFormErrors({});

      // Validate form data
      const validatedData = demoBookingSchema.parse(formData);
      console.log("Booking demo with validated data:", validatedData);

      // Handle demo booking logic here
      // e.g., API call to book demo

    } catch (error) {
      if (error instanceof z.ZodError) {
        const errors: Record<string, string> = {};
        error.errors.forEach((err) => {
          if (err.path.length > 0) {
            errors[err.path[0] as string] = err.message;
          }
        });
        setFormErrors(errors);
      }
    }
  };

  return (
    <div className="min-h-screen">
      <Navbar />
      
      {/* Hero Section */}
      <div className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Book a demo
            </h1>
            <div className="flex items-center justify-center gap-8 text-lg text-gray-700">
              <div className="flex items-center gap-2">
                <Search className="h-5 w-5 text-purple-600" />
                <span>Let us Match You</span>
              </div>
              <span className="text-2xl">OR</span>
              <div className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-purple-600" />
                <span>Search and Book Yourself</span>
              </div>
            </div>
          </div>

          {/* Main Content Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2 max-w-2xl mx-auto mb-8 h-14 p-1 bg-white border border-gray-200 rounded-lg shadow-sm">
              <TabsTrigger
                value="match"
                className="flex items-center justify-center gap-2 h-full px-6 py-3 text-sm font-medium rounded-md transition-all duration-200 data-[state=active]:bg-purple-600 data-[state=active]:text-white data-[state=active]:shadow-sm data-[state=inactive]:text-gray-600 data-[state=inactive]:hover:text-gray-900 data-[state=inactive]:hover:bg-gray-50"
              >
                <Sparkles className="h-4 w-4" />
                <span>Let Us Match You</span>
              </TabsTrigger>
              <TabsTrigger
                value="search"
                className="flex items-center justify-center gap-2 h-full px-6 py-3 text-sm font-medium rounded-md transition-all duration-200 data-[state=active]:bg-purple-600 data-[state=active]:text-white data-[state=active]:shadow-sm data-[state=inactive]:text-gray-600 data-[state=inactive]:hover:text-gray-900 data-[state=inactive]:hover:bg-gray-50"
              >
                <Search className="h-4 w-4" />
                <span>Find Your Tutor</span>
              </TabsTrigger>
            </TabsList>

            {/* Let Us Match You Tab */}
            <TabsContent value="match">
              <div className="grid lg:grid-cols-2 gap-12 items-start">
                {/* Left Side - Benefits */}
                <div className="space-y-8">
                  <Card className="border-2 border-purple-200 bg-white/80 backdrop-blur-sm">
                    <CardContent className="p-8">
                      <div className="text-center mb-6">
                        <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-full mb-4">
                          <Sparkles className="h-8 w-8 text-purple-600" />
                        </div>
                        <h2 className="text-2xl font-bold text-gray-900 mb-2">Let Us Match You</h2>
                        <p className="text-gray-600">
                          Tell us your needs and preferences, and we'll match you with the perfect tutor for your demo session.
                        </p>
                      </div>
                      
                      <div className="space-y-4">
                        <div className="flex items-center gap-3">
                          <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                          <span className="text-gray-700">Save time on searching</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                          <span className="text-gray-700">Expert matching based on your needs</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                          <span className="text-gray-700">Minimal effort required</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Student or parent info - Form */}
                <Card className="border border-gray-200 shadow-lg">
                  <CardContent className="p-6">
                    <StudentParentForm
                      formData={{
                        parentName: formData.parentName,
                        studentName: formData.studentName,
                        parentPhone: formData.parentPhone,
                        email: formData.email,
                        grade: formData.grade,
                        primaryGoals: formData.primaryGoals,
                      }}
                      formErrors={formErrors}
                      onInputChange={handleInputChange}
                      onCheckboxChange={handleCheckboxChange}
                      showGradeSelection={true}
                      showPrimaryGoals={true}
                    />
                    
                    <div className="mt-4">
                      <Button
                        onClick={handleBookDemo}
                        className="w-full text-white py-3 text-lg font-semibold"
                        style={{ backgroundColor: '#ff7f52' }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e6704a'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#ff7f52'}
                      >
                        BOOK A FREE DEMO
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Find Your Tutor Tab */}
            <TabsContent value="search">
            </TabsContent>
          </Tabs>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default TutorSearchRedesigned;
